import { Badge, IconButton, useTheme } from '@mui/material';
import { Notification } from 'iconsax-react';
import { type FC, useEffect, useRef, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { UNREAD_FILTER } from '@core/utils/notificationQueryParams';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import { useNotificationPanel } from '@web/providers/NotificationPanelProvider/NotificationPanelProvider';

export const NotificationBell: FC = () => {
  const theme = useTheme();
  const { toggle } = useNotificationPanel();

  const { data: unread } = useSlabQuery('GET notifications', {
    queryParams: { perPage: 1, page: 0, filterBy: [UNREAD_FILTER] },
  });

  const count = unread?.count;
  const previousCount = useRef<number | undefined>(undefined);
  const [pulses, setPulses] = useState(0);
  useEffect(() => {
    if (!hasValue(count)) {
      return undefined;
    }
    if (hasValue(previousCount.current) && count > previousCount.current) {
      setPulses((current) => current + 1);
    }
    previousCount.current = count;
    return undefined;
  }, [count]);

  return (
    <IconButton onClick={toggle}>
      <Badge
        key={pulses}
        badgeContent={count ?? 0}
        max={99}
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: 'SlabIndigo.500',
            color: 'SlabGray.0',
            ...(pulses > 0 && { animation: 'notificationBadgePulse 0.6s ease-out' }),
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          },
          '@keyframes notificationBadgePulse': {
            '50%': { transform: 'scale(1.5) translate(50%, -50%)' },
          },
        }}
      >
        <Notification variant='TwoTone' color={theme.palette.SlabGray[800]} />
      </Badge>
    </IconButton>
  );
};
